import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Moon, Droplets, Leaf, Sun, Settings2, CalendarPlus } from "lucide-react";

const PHASE_ICONS = { menstrual: Droplets, follicular: Leaf, ovulation: Sun, luteal: Moon };
const TOPICS = [
  ["bloating", "Bloating"],
  ["cravings", "Hunger & cravings"],
  ["cramps", "Cramps"],
  ["low_energy", "Low energy"],
  ["mood_sleep", "Mood & sleep"],
];
const today = () => new Date().toISOString().slice(0, 10);

export default function ClientCycle() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [topic, setTopic] = useState("");
  const [periodDate, setPeriodDate] = useState(today());
  const [form, setForm] = useState({ last_period_start: "", cycle_length: 28, period_length: 5 });

  const load = () => api.get("/cycle").then((r) => {
    setData(r.data);
    if (r.data.settings) {
      setForm({
        last_period_start: r.data.settings.last_period_start,
        cycle_length: r.data.settings.cycle_length,
        period_length: r.data.settings.period_length,
      });
    }
  });
  useEffect(() => { load().catch(() => {}); }, []);

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      await api.put("/cycle/settings", {
        ...form,
        cycle_length: parseInt(form.cycle_length, 10) || 28,
        period_length: parseInt(form.period_length, 10) || 5,
      });
      toast.success("Cycle tracking updated");
      setEditing(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const logPeriod = async () => {
    try {
      await api.post("/cycle/period", { date: periodDate });
      toast.success("Period logged — cycle updated");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!data) return null;

  const setupMode = !data.configured || editing;
  const Icon = PHASE_ICONS[data.phase] || Moon;

  return (
    <div className="space-y-8 lg:mt-0 mt-10" data-testid="client-cycle">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Your body, in context</div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3">My cycle</h1>
        </div>
        {data.configured && !editing && (
          <button className="btn-secondary text-xs" onClick={() => setEditing(true)} data-testid="cycle-settings-btn">
            <Settings2 size={13} /> Period dates & settings
          </button>
        )}
      </header>

      {setupMode ? (
        <form onSubmit={saveSettings} className="card-luxe space-y-5 max-w-xl" data-testid="cycle-setup-form">
          <div className="overline">{data.configured ? "Update settings" : "Set up cycle tracking"}</div>
          <p className="text-sm text-secondary-luxe">
            Add the first day of your last period and your usual cycle length. We'll estimate your
            phase and offer flexible food + training ideas for the body you're in today.
          </p>
          <div>
            <label className="label-luxe">First day of last period</label>
            <input type="date" required className="input-luxe" value={form.last_period_start}
              onChange={(e) => setForm({ ...form, last_period_start: e.target.value })} data-testid="cycle-last-period" />
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="label-luxe">Cycle length (days)</label>
              <input type="number" min="20" max="45" className="input-luxe" value={form.cycle_length}
                onChange={(e) => setForm({ ...form, cycle_length: e.target.value })} data-testid="cycle-length" />
            </div>
            <div>
              <label className="label-luxe">Period length (days)</label>
              <input type="number" min="1" max="10" className="input-luxe" value={form.period_length}
                onChange={(e) => setForm({ ...form, period_length: e.target.value })} data-testid="period-length" />
            </div>
          </div>
          <div className="flex gap-3">
            <button className="btn-primary" data-testid="cycle-save-btn">Save</button>
            {data.configured && <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>Cancel</button>}
          </div>
        </form>
      ) : (
        <>
          <section className="card-luxe" data-testid="cycle-phase-card">
            <div className="flex items-start justify-between flex-wrap gap-6">
              <div>
                <div className="overline flex items-center gap-2">
                  <Icon size={13} style={{ color: "var(--accent)" }} /> Estimated phase
                </div>
                <h2 className="font-serif text-4xl mt-3" data-testid="cycle-phase-label">{data.guide?.label} phase</h2>
                <p className="font-script text-2xl mt-1" style={{ color: "var(--accent)" }}>{data.guide?.tagline}</p>
                <p className="text-sm text-secondary-luxe mt-4 max-w-xl leading-relaxed">{data.guide?.about}</p>
              </div>
              <div className="text-right">
                <div className="font-mono-luxe text-6xl" data-testid="cycle-day">{data.cycle_day}</div>
                <div className="overline mt-1">Cycle day</div>
                <div className="text-xs text-tertiary-luxe mt-3" data-testid="next-period">
                  Next period est. {data.next_period}<br />in {data.days_until_next} days · timing can vary
                </div>
              </div>
            </div>
          </section>

          <section className="card-luxe" data-testid="cycle-topics">
            <div className="overline">What would help today?</div>
            <p className="text-xs text-tertiary-luxe mt-2">Choose a topic for phase-specific tips. This doesn't log a symptom.</p>
            <div className="flex gap-2 flex-wrap mt-4">
              {TOPICS.map(([key, label]) => (
                <button key={key} onClick={() => setTopic(topic === key ? "" : key)}
                  data-testid={`topic-${key}`}
                  className={topic === key ? "btn-primary text-xs" : "btn-secondary text-xs"}>{label}</button>
              ))}
            </div>
            {topic && (
              <div className="mt-5 p-5" style={{ background: "var(--surface-2)" }} data-testid="topic-tip">
                <div className="overline">Try this</div>
                <p className="text-sm mt-2 leading-relaxed">{data.guide?.topics?.[topic]}</p>
              </div>
            )}
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <section className="card-luxe" data-testid="cycle-nutrition-tips">
              <div className="overline">Nourish this phase</div>
              <ul className="mt-4 space-y-3">
                {(data.guide?.nutrition || []).map((t) => (
                  <li key={t.slice(0, 40)} className="text-sm text-secondary-luxe flex gap-3">
                    <span style={{ color: "var(--accent)" }}>—</span> {t}
                  </li>
                ))}
              </ul>
            </section>
            <section className="card-luxe" data-testid="cycle-training-tips">
              <div className="overline">Train this phase</div>
              <ul className="mt-4 space-y-3">
                {(data.guide?.training || []).map((t) => (
                  <li key={t.slice(0, 40)} className="text-sm text-secondary-luxe flex gap-3">
                    <span style={{ color: "var(--accent)" }}>—</span> {t}
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <section className="card-luxe" data-testid="log-period-card">
            <div className="overline flex items-center gap-2"><CalendarPlus size={13} /> Period started?</div>
            <p className="text-sm text-secondary-luxe mt-2">Log day one and your cycle recalculates instantly.</p>
            <div className="flex gap-3 mt-4 flex-wrap">
              <input type="date" className="input-luxe max-w-[200px]" value={periodDate}
                onChange={(e) => setPeriodDate(e.target.value)} data-testid="period-date-input" />
              <button className="btn-primary" onClick={logPeriod} data-testid="log-period-btn">Log period</button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
